import React from 'react';

import {
  AutoParameterField,
  CheckboxField,
  DateField,
  DropdownField,
  EtaField,
  GetParameterField,
  LabourTimeField,
  PresentationField,
  RadioField,
  ServiceContextRequired,
  TextField_,
  UnsupportedField,
  UploadField,
} from './interactionFieldComponents';
import { FormFieldItem, FormFieldType } from '../../../../@types';
import { ParameterData } from '../../../../@types/form-builder.type';
import { ServiceObject } from '../../../../@types/service.type';
import {
  InteractionFaultSelectionField,
  InteractionGeoLocationField,
  InteractionParameterField,
  InteractionPartsRequestField,
  InteractionPartsRequestValue,
  InteractionSignatureField,
  InteractionSolutionActionField,
} from '../../../../components/organisms/InteractionFields';
import { ServiceAttachmentUploadFeature } from '../../../../configs/enums';
import { ServiceAttachmentMimeTypes } from '../../../../configs/uploaded-files';

export interface FieldRendererProps {
  field: FormFieldItem;
  onChange: (next: FormFieldItem) => void;
  serviceId?: number;
  /**
   * The full service, needed by the field types that query against it
   * (FaultSelection, SolutionAction, PartsRequest). Callers that can't supply it
   * get an explicit "service context required" note rather than a broken field.
   */
  service?: ServiceObject;
}

/**
 * The shared `Interaction*Field` organisms take a bare value and report a bare
 * value; this renderer's contract patches the whole field. This adapts between
 * the two so the submitted shape is unchanged.
 */
const patchValue =
  ({ field, onChange }: FieldRendererProps) =>
  (value: unknown): void =>
    onChange({ ...field, value } as FormFieldItem);

export const fieldHasValue = (field: FormFieldItem): boolean => {
  if (field.value === undefined || field.value === null) return false;
  if (typeof field.value === 'string') return field.value.trim() !== '';
  if (typeof field.value === 'boolean') return field.value;
  if (Array.isArray(field.value)) return field.value.length > 0;
  return true;
};

// A flat switch is the clearest shape for a dispatcher, and the complexity is just
// the field-type count (22). Splitting it would scatter the mapping across helpers
// for no reader benefit; the per-type behaviour already lives in its own component.
// eslint-disable-next-line complexity
export const renderField = (
  field: FormFieldItem,
  onChange: (next: FormFieldItem) => void,
  serviceId: number | undefined,
  service?: ServiceObject
) => {
  const props: FieldRendererProps = { field, onChange, serviceId, service };
  switch (field.type) {
    case FormFieldType.Text:
    case FormFieldType.RichTextbox:
      return <TextField_ {...props} />;
    case FormFieldType.Date:
      return <DateField {...props} />;
    case FormFieldType.LabourTime:
      return <LabourTimeField {...props} />;
    case FormFieldType.Checkbox:
      return <CheckboxField {...props} />;
    case FormFieldType.Dropdown:
      return <DropdownField {...props} />;
    case FormFieldType.Radio:
      return <RadioField {...props} />;
    case FormFieldType.Photo:
      return (
        <UploadField
          {...props}
          feature={ServiceAttachmentUploadFeature.InteractionPhotoUpload}
          mimeTypes={ServiceAttachmentMimeTypes.InteractionPhotoUpload}
        />
      );
    case FormFieldType.File:
      return (
        <UploadField
          {...props}
          feature={ServiceAttachmentUploadFeature.InteractionFileUpload}
          mimeTypes={ServiceAttachmentMimeTypes.InteractionFileUpload}
        />
      );
    case FormFieldType.MerchantDamageTemplate:
      return (
        <UploadField
          {...props}
          feature={ServiceAttachmentUploadFeature.InteractionPhotoUpload}
          mimeTypes={ServiceAttachmentMimeTypes.InteractionPhotoUpload}
        />
      );
    case FormFieldType.Label:
    case FormFieldType.Presentation:
      return <PresentationField {...props} />;
    case FormFieldType.EstimatedTimeOfArrival:
      return <EtaField {...props} />;

    // Applied by the backend from the template — nothing to collect.
    case FormFieldType.SetParameterNoUI:
    case FormFieldType.ActionTrigger:
      return null;

    case FormFieldType.SetParameter:
      return field.preSelectedData ? (
        <InteractionParameterField
          preSelectedData={field.preSelectedData as ParameterData}
          value={field.value}
          onChange={patchValue(props)}
          isRequired={field.isRequired}
        />
      ) : (
        <AutoParameterField {...props} />
      );

    case FormFieldType.GetParameter:
      return <GetParameterField {...props} />;

    // Block renders nothing here, exactly as the legacy FormGenerator does — it
    // filters Block out of its field list (FormGenerator.tsx:258). The block is
    // enforced outside the renderer by `blockInteractionAvailable` and
    // `getBlockInteractionMessage` in utils/interactions.ts, both gated on
    // configure.interactions.bypass_mandatory, and on the status dialog by its
    // permission-gated Skip. Rendering a bypass control here would ADD an
    // affordance desktop has never had, on a security-sensitive path.
    //
    // Mobile deliberately differs: its sheet has no Skip, so
    // InteractionBlockField gives it a per-field bypass behind the same
    // permission, and MobileStatusUpdateDialog disables Submit while blocked.
    case FormFieldType.Block:
      return null;

    case FormFieldType.FaultSelection:
      return props.service ? (
        <InteractionFaultSelectionField
          service={props.service}
          title={field.title}
          value={(field.value as string | undefined) ?? ''}
          onChange={patchValue(props)}
          isRequired={field.isRequired}
        />
      ) : (
        <ServiceContextRequired {...props} />
      );

    // Deliberately NOT folded in with FaultSelection above. The two look alike in a
    // template but draw from different sources: a solution action is one of the job's
    // part actions, a fault selection is an observed fault code. Sharing the renderer
    // silently collected the wrong answer.
    case FormFieldType.SolutionAction:
      return props.service ? (
        <InteractionSolutionActionField
          service={props.service}
          value={field.value as string | undefined}
          onChange={patchValue(props)}
          isRequired={field.isRequired}
        />
      ) : (
        <ServiceContextRequired {...props} />
      );

    case FormFieldType.PartsRequest:
      return props.service ? (
        <InteractionPartsRequestField
          service={props.service}
          title={field.title || 'Part Type'}
          isRequired={field.isRequired}
          value={field.value as InteractionPartsRequestValue | undefined}
          onChange={patchValue(props)}
        />
      ) : (
        <ServiceContextRequired {...props} />
      );

    case FormFieldType.Signature:
      return (
        <InteractionSignatureField
          title={field.title}
          isRequired={field.isRequired}
          value={field.value}
          onChange={patchValue(props)}
        />
      );

    case FormFieldType.GetGeoLocation:
      return (
        <InteractionGeoLocationField
          title={field.title}
          value={field.value}
          onChange={patchValue(props)}
        />
      );

    default:
      return <UnsupportedField {...props} />;
  }
};
